<template>
  <div class="record-transcription">
    <div class="rt-head">
      <span class="rt-eyebrow">Gravação da consulta</span>
      <h6>Transcrição automática</h6>
      <p>
        Grave a conversa com o paciente e a IA devolve a nota do atendimento
        para você revisar antes de lançar na anamnese.
      </p>
    </div>

    <div class="empty" v-if="!loading && !context.enabled">
      <div class="empty-icon">
        <fa-icon :icon="['fal', 'microphone-slash']" size="2x" />
      </div>
      <p class="empty-title h6">Recurso indisponível</p>
      <p class="empty-subtitle">A gravação não está liberada para este atendimento.</p>
    </div>

    <template v-else-if="context.enabled">
      <div class="rt-plan" :class="{ exceeded: quotaExhausted, warning: quotaWarning }">
        <fa-icon
          :icon="['fal', quotaExhausted || quotaWarning ? 'exclamation-triangle' : 'sparkles']"
        />
        <div class="rt-plan-text">
          <strong>{{ planLabel }}</strong>
          <span>{{ usageLabel }}</span>

          <button
            type="button"
            class="btn btn-action btn-icon btn-sm tooltip"
            data-tooltip="Atualizar saldo"
            :class="{ loading: refreshing }"
            :disabled="refreshing"
            @click="refreshQuota"
          ><fa-icon :icon="['fal', 'sync']" v-if="!refreshing" /></button>
        </div>

        <div class="rt-plan-actions">
          <button
            type="button"
            class="btn btn-sm btn-link rt-plan-buy"
            v-if="canManageAccount"
            @click="openMinutes"
          >Contratar mais minutos</button>
        </div>
      </div>

      <div class="rt-consent" v-if="!consent">
        <div class="rt-consent-icon">
          <fa-icon :icon="['fal', 'shield-check']" />
        </div>

        <div class="rt-consent-body">
          <h6>Autorização do paciente</h6>
          <p class="rt-consent-help">Leia para o paciente antes de começar:</p>

          <blockquote class="rt-term">{{ context.term.text }}</blockquote>

          <div class="form-group" :class="{ 'has-error': $v.accepted.$error }">
            <label class="form-checkbox rt-check">
              <input type="checkbox" v-model="accepted" @change="$v.accepted.$touch()">
              <i class="form-icon" />
              {{ context.term.confirmation }}
            </label>
            <div class="form-input-hint" v-if="$v.accepted.$error">Campo obrigatório</div>
          </div>

          <button
            type="button"
            class="btn btn-primary"
            :class="{ loading: saving }"
            :disabled="saving || readonly || !accepted"
            @click="acceptConsent"
          >Registrar autorização</button>
        </div>
      </div>

      <div class="rt-recorder" v-else>
        <div class="rt-recorder-head">
          <span class="rt-pill done">
            <fa-icon :icon="['fal', 'check-circle']" />
            Autorizado em {{ consent.acceptedAt | date('datetime') }}
          </span>
          <button
            type="button"
            class="btn btn-link btn-sm"
            v-if="!readonly && items.length === 0"
            @click="revokeConsent"
          >Remover autorização</button>
        </div>

        <div class="rt-recorder-body" v-if="!readonly">
          <template v-if="recording">
            <div class="rt-timer">
              <span class="rt-dot" :class="{ live: !paused }" />
              <strong>{{ elapsedLabel }}</strong>
              <span class="rt-timer-state">
                <template v-if="paused">Pausado</template>
                <template v-else>
                  Gravando — o áudio é salvo a cada {{ blockLabel }}, sem interromper
                </template>
              </span>
            </div>

            <div class="rt-meter" :class="{ live: !paused }">
              <span v-for="n in meterBars" :key="n" :class="{ on: level >= n * 7 }" />
            </div>
          </template>

          <div class="rt-actions">
            <template v-if="!recording">
              <button
                type="button"
                class="btn btn-icon btn-icon-left btn-primary"
                :class="{ loading: starting }"
                :disabled="starting || uploading || quotaBlocked"
                @click="start"
              >
                <fa-icon :icon="['fal', 'microphone']" /> Iniciar gravação
              </button>

              <label
                class="btn btn-icon btn-icon-left btn-secondary rt-file"
                :class="{ disabled: quotaBlocked }"
              >
                <fa-icon :icon="['fal', 'upload']" /> Enviar arquivo
                <input type="file" accept="audio/*" :disabled="quotaBlocked" @change="uploadFile">
              </label>
            </template>

            <template v-else>
              <button
                type="button"
                class="btn btn-icon btn-icon-left"
                :class="paused ? 'btn-secondary' : 'btn-gray-outline'"
                @click="togglePause"
              >
                <fa-icon :icon="['fal', paused ? 'play' : 'pause']" />
                {{ paused ? 'Retomar' : 'Pausar' }}
              </button>
              <button
                type="button"
                class="btn btn-icon btn-icon-left btn-error"
                @click="stop"
              >
                <fa-icon :icon="['fal', 'stop']" /> Parar e transcrever
              </button>
            </template>
          </div>
        </div>

        <div class="rt-recorder-foot">
          Áudio e transcrição são excluídos automaticamente {{ retentionLabel }}.
          Durante a gravação o áudio é salvo a cada {{ blockLabel }}
          e transcrito de uma vez ao parar.
        </div>
      </div>

      <div class="rt-uploading" v-if="sendingAudio">
        <div class="loading" />
        <span>Enviando o áudio para transcrição...</span>
      </div>

      <div class="rt-list" v-if="items.length > 0">
        <div class="rt-list-head">
          <span>Gravações deste atendimento</span>
          <span class="rt-count">{{ items.length }}</span>
          <span class="rt-live" v-if="recording">
            <span class="rt-dot live" />
            A consulta continua sendo gravada
          </span>
        </div>

        <div
          class="rt-item"
          :class="{ expanded: expanded === item.id }"
          v-for="(item, index) in items"
          :key="item.id"
        >
          <div class="rt-row" @click="toggle(item)">
            <span class="rt-row-icon" :class="item.status">
              <fa-icon :icon="['fal', statusIcon(item)]" :spin="item.status === 'processing'" />
            </span>

            <div class="rt-row-info">
              <strong>Gravação {{ index + 1 }}</strong>
              <small>
                {{ item.createdAt | date('datetime') }}
                <template v-if="item.seconds"> · {{ durationLabel(item.seconds) }}</template>
                <template v-if="item.purgedAt"> · conteúdo expurgado</template>
              </small>
            </div>

            <span class="rt-pill" :class="item.status">
              <fa-icon :icon="['fal', statusIcon(item)]" />
              {{ statusLabel(item) }}
            </span>

            <div class="rt-row-actions" @click.stop>
              <button
                type="button"
                class="btn btn-action btn-icon btn-sm tooltip tooltip-left"
                data-tooltip="Ouvir o áudio"
                v-if="item.hasAudio"
                @click="play(item)"
              ><fa-icon :icon="['fal', 'volume-up']" /></button>
              <button
                type="button"
                class="btn btn-action btn-icon btn-sm tooltip tooltip-left"
                data-tooltip="Transcrever de novo"
                v-if="canReprocess(item)"
                @click="reprocess(item)"
              ><fa-icon :icon="['fal', 'redo']" /></button>
              <button
                type="button"
                class="btn btn-action btn-icon btn-sm tooltip tooltip-left rt-danger"
                :data-tooltip="removeTooltip(item)"
                :disabled="recording || item.status === 'processing'"
                v-if="!readonly"
                @click="remove(item)"
              ><fa-icon :icon="['fal', 'trash']" /></button>
            </div>

            <fa-icon class="rt-row-chevron" :icon="['fal', 'chevron-right']" />
          </div>

          <div class="rt-detail" v-if="expanded === item.id">
            <audio
              class="rt-audio"
              v-if="audioUrls[item.id]"
              :src="audioUrls[item.id]"
              controls
              autoplay
            />

            <div class="rt-detail-error" v-if="item.status === 'failed' || item.error">
              <fa-icon :icon="['fal', 'exclamation-triangle']" />
              <span>
                <template v-if="item.status === 'failed'">
                  {{ item.error || 'Não foi possível transcrever o áudio.' }}
                </template>
                <template v-else>
                  A última tentativa falhou ({{ item.error }}). Use "Transcrever de novo".
                </template>
              </span>
            </div>

            <div class="rt-detail-waiting" v-else-if="item.status !== 'done'">
              <div class="loading" />
              <span>{{ statusLabel(item) }} — o texto aparece aqui quando terminar.</span>
            </div>

            <template v-if="item.note">
              <div class="rt-detail-title">
                <span>Nota da consulta</span>
                <span class="rt-tag">IA</span>
              </div>
              <div class="rt-text">{{ item.note }}</div>

              <div class="rt-detail-actions">
                <button
                  type="button"
                  class="btn btn-primary btn-sm"
                  v-if="!readonly"
                  @click="review(item)"
                >Revisar e aplicar na anamnese</button>

                <button
                  type="button"
                  class="btn btn-link btn-sm"
                  v-if="item.text"
                  @click="showText = showText === item.id ? null : item.id"
                >
                  {{ showText === item.id ? 'Ocultar transcrição' : 'Ver transcrição completa' }}
                </button>
              </div>
            </template>

            <div class="rt-raw" v-if="showText === item.id">
              <div class="rt-raw-head">
                <span>Transcrição do áudio</span>
                <small>o que a IA entendeu, sem edição</small>
              </div>
              <div class="rt-text raw">{{ item.text }}</div>
            </div>
          </div>
        </div>
      </div>

      <st-feedback
        context="recording"
        label="Algum problema com a transcrição ou alguma sugestão? Fale com a gente"
        placeholder="Conte o que aconteceu na gravação, na transcrição ou na nota..."
        :options="feedbackOptions"
      />

      <div class="modal active" v-if="reviewSummary">
        <a href="#close" class="modal-overlay" @click="reviewSummary = null"></a>
        <div class="modal-container modal-lg nr-modal">
          <noa-review
            :summary="reviewSummary"
            :inputs="inputs"
            @apply="apply"
            @discard="reviewSummary = null"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { MANAGE_CUSTOMER_ACCOUNT } from '@/data/actions/modules/general';
import NoaReview from '../noa/Review.vue';

const BLOCK_SECONDS = 600;
const SLICE_SECONDS = 10;
// teto de segurança: gravação esquecida ligada não vira uma tarde inteira de áudio
const MAX_SECONDS = 5400;
const POLL_INTERVAL = 5000;
const RETRY_INTERVAL = 15000;
const RETRY_STATUSES = [408, 409, 429];
const MIME_TYPES = [
  'audio/mp4;codecs=opus',
  'audio/mp4',
  'audio/ogg;codecs=opus',
  'audio/webm;codecs=opus',
  'audio/webm',
];
const STATUS_LABELS = {
  recording: 'Gravando',
  pending: 'Na fila',
  processing: 'Transcrevendo...',
  done: 'Transcrita',
  failed: 'Falha na transcrição',
};
const STATUS_ICONS = {
  recording: 'microphone',
  pending: 'clock',
  processing: 'spinner-third',
  done: 'check-circle',
  failed: 'exclamation-triangle',
};

export default {
  components: { NoaReview },
  props: {
    medicalRecordId: {
      type: String,
      required: true,
    },
    inputs: {
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    initial: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      starting: false,
      uploading: false,
      accepted: false,
      recording: false,
      paused: false,
      elapsed: 0,
      blockElapsed: 0,
      expanded: null,
      showText: null,
      audioUrls: {},
      reviewSummary: null,
      refreshing: false,
      recorder: null,
      stream: null,
      session: null,
      queue: [],
      sending: false,
      retryTimer: null,
      droppedSession: null,
      timer: null,
      poll: null,
      level: 0,
      meterBars: 14,
      audioContext: null,
      analyser: null,
      sourceNode: null,
      meterData: null,
      meterTimer: null,
      feedbackOptions: [
        { value: 'complaint', label: 'Falha na gravação ou transcrição' },
        { value: 'suggestion', label: 'Tenho uma sugestão de melhoria' },
        { value: 'praise', label: 'Quero elogiar o recurso' },
      ],
      context: {
        enabled: false,
        consent: null,
        term: { text: '' },
        items: [],
        retentionDays: 30,
      },
    };
  },
  validations: {
    accepted: {
      checked: value => value === true,
    },
  },
  computed: {
    // só quem administra a conta contrata minutos; os demais falam com o gestor
    canManageAccount() {
      return this.$can(MANAGE_CUSTOMER_ACCOUNT);
    },
    consent() {
      return this.context.consent;
    },
    items() {
      return this.context.items || [];
    },
    blockLabel() {
      if (BLOCK_SECONDS < 60) {
        return `${BLOCK_SECONDS} segundos`;
      }

      return `${BLOCK_SECONDS / 60} minutos`;
    },
    retentionLabel() {
      const days = this.context.retentionDays || 30;
      return days === 1 ? 'em 1 dia' : `em ${days} dias`;
    },
    elapsedLabel() {
      const minutes = Math.floor(this.elapsed / 60);
      const seconds = this.elapsed % 60;
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    },
    quota() {
      return this.context.quota || {};
    },
    quotaExhausted() {
      const { limit, remaining, enabled } = this.quota;

      // sem franquia nenhuma o recurso só está liberado enquanto a cota não vale
      if (!limit) {
        return !!enabled;
      }

      return (remaining || 0) <= 0;
    },
    // o limite só impede de gravar quando o controle de cota está ligado
    quotaBlocked() {
      return this.quotaExhausted && !!this.quota.enabled;
    },
    quotaWarning() {
      const { limit, used } = this.quota;

      return !this.quotaExhausted && !!limit && (used / limit) >= 0.8;
    },
    // franquia consumida acima do limite atual acontece quando o parâmetro muda no
    // meio do mês: o número exibido acompanha o que já foi usado
    freeShown() {
      return Math.max(this.quota.freeLimit || 0, this.quota.usedFree || 0);
    },
    limitShown() {
      return this.freeShown + (this.quota.paidLimit || 0);
    },
    // com minutos contratados, a composição do limite evita a leitura de que os
    // minutos grátis foram descontados do pacote
    limitBreakdown() {
      const { paidLimit } = this.quota;

      if (!paidLimit) {
        return '';
      }

      return ` · ${this.freeShown} grátis + ${paidLimit} contratados`;
    },
    planLabel() {
      if (this.quotaExhausted) {
        return this.quota.limit ? 'Minutos do mês esgotados' : 'Sem minutos disponíveis';
      }

      if (this.quotaWarning) {
        return 'Seus minutos estão acabando';
      }

      return this.quota.free ? 'Transcrição no plano grátis' : 'Transcrição por IA';
    },
    usageLabel() {
      const used = this.quota.used || 0;

      if (this.quotaExhausted) {
        if (!this.quota.limit) {
          return 'Esta conta não tem minutos de transcrição. Contrate minutos para gravar.';
        }

        return `Você usou os ${this.quota.limit} minutos deste mês.`
          + ' Contrate mais minutos ou aguarde a virada do mês.';
      }

      if (this.quotaWarning) {
        const { remaining } = this.quota;

        return `${used} de ${this.quota.limit} minutos usados — restam`
          + ` ${remaining} ${remaining === 1 ? 'minuto' : 'minutos'} neste mês.`;
      }

      if (this.limitShown) {
        return `${used} de ${this.limitShown} minutos usados neste mês${this.limitBreakdown}`;
      }

      return `${used} ${used === 1 ? 'minuto usado' : 'minutos usados'} neste mês`;
    },
    sendingAudio() {
      return this.uploading || (!this.recording && this.queue.length > 0);
    },
    pendingItems() {
      return this.items.filter(item => ['pending', 'processing'].includes(item.status));
    },
  },
  watch: {
    // a autorização some quando a última gravação é excluída: sem parar aqui, o
    // painel sai da tela e o microfone continuaria aberto sem nenhum controle
    consent(value) {
      if (!value && this.recording) {
        this.stop();
      }
    },
  },
  mounted() {
    window.addEventListener('beforeunload', this.warnOnLeave);

    if (this.initial && this.initial.enabled) {
      this.context = this.initial;
      this.schedulePoll();
      return;
    }

    this.load();
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.warnOnLeave);

    // sair do prontuário com a gravação ligada fecha o bloco e envia o que já foi gravado,
    // em vez de deixar o microfone aberto no atendimento seguinte
    if (this.recording) {
      this.stop();
    }

    this.clearTimers();
    this.stopMeter();
    this.releaseStream();
  },
  methods: {
    load(silent = false) {
      if (!this.medicalRecordId) {
        return Promise.resolve();
      }

      this.loading = !silent;

      return this.$httpX
        .get(`/medical-records/${this.medicalRecordId}/transcriptions`)
        .then(({ data }) => {
          this.context = data;
          this.schedulePoll();
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    schedulePoll() {
      clearTimeout(this.poll);

      if (this.pendingItems.length === 0) {
        return;
      }

      this.poll = setTimeout(() => this.load(true), POLL_INTERVAL);
    },
    acceptConsent() {
      this.$v.accepted.$touch();

      if (this.$v.accepted.$invalid) {
        return;
      }

      this.saving = true;

      this.$http
        .post(`/medical-records/${this.medicalRecordId}/transcriptions/consent`)
        .then(({ data }) => {
          this.context.consent = data;
          this.$toast.success('Autorização registrada');
        })
        .catch(this.showError)
        .finally(() => {
          this.saving = false;
        });
    },
    revokeConsent() {
      this.$http
        .delete(`/medical-records/${this.medicalRecordId}/transcriptions/consent`)
        .then(() => {
          this.context.consent = null;
          this.accepted = false;
          this.$v.accepted.$reset();
        })
        .catch(this.showError);
    },
    async start() {
      if (!navigator.mediaDevices || !window.MediaRecorder) {
        this.$toast.show('Este navegador não permite gravar áudio.', { type: 'error' });
        return;
      }

      // dois cliques antes do microfone abrir deixariam um stream vivo sem ninguém parando
      if (this.starting || this.recording) {
        return;
      }

      this.starting = true;

      try {
        this.releaseStream();

        this.stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
          },
        });

        this.elapsed = 0;
        this.blockElapsed = 0;
        this.recording = true;
        this.paused = false;
        this.$emit('recording', true);

        this.startRecorder();
        this.startTimer();
        this.startMeter();
      } catch (e) {
        this.$toast.show('Não foi possível acessar o microfone.', { type: 'error' });
      } finally {
        this.starting = false;
      }
    },
    startRecorder() {
      const mimeType = MIME_TYPES.find(type => window.MediaRecorder.isTypeSupported(type));

      const recorder = new window.MediaRecorder(this.stream, {
        mimeType,
        audioBitsPerSecond: 32000,
      });
      const session = {
        id: crypto.randomUUID(),
        medicalRecordId: this.medicalRecordId,
        mimeType: recorder.mimeType.split(';')[0],
        part: 0,
        chunks: [],
        startedAt: this.elapsed,
      };

      this.recorder = recorder;
      this.session = session;

      // a sessão é lida da closure: quando este evento chega, this.session já
      // pode ser a gravação seguinte, ou nula depois de liberar o microfone
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          session.chunks.push(event.data);
        }
      };

      recorder.onstop = () => {
        this.closeBlock(session);

        if (session.part === 0 || session.id === this.droppedSession) {
          return;
        }

        this.queue.push({
          medicalRecordId: session.medicalRecordId,
          sessionId: session.id,
          finish: true,
        });
        this.flush();
      };

      recorder.start(SLICE_SECONDS * 1000);
    },
    closeBlock(session) {
      const seconds = Math.max(this.elapsed - session.startedAt, 0);
      const blob = new Blob(session.chunks, { type: session.mimeType });

      session.startedAt = this.elapsed;
      session.chunks = [];

      if (session.id === this.droppedSession || blob.size === 0) {
        return;
      }

      this.queue.push({
        medicalRecordId: session.medicalRecordId,
        sessionId: session.id,
        part: session.part,
        seconds,
        blob,
        fileName: `gravacao-${Date.now()}.${this.extensionFor(session.mimeType)}`,
      });
      session.part += 1;
      this.flush();
    },
    startTimer() {
      clearInterval(this.timer);

      this.timer = setInterval(() => {
        if (this.paused) {
          return;
        }

        this.elapsed += 1;
        this.blockElapsed += 1;

        if (this.elapsed >= MAX_SECONDS) {
          this.stop();
          this.$toast.show(
            `Gravação encerrada automaticamente após ${MAX_SECONDS / 60} minutos.`,
            { type: 'warning' },
          );
          return;
        }

        if (this.blockElapsed >= BLOCK_SECONDS) {
          this.saveBlock();
        }
      }, 1000);
    },
    saveBlock() {
      this.blockElapsed = 0;
      this.closeBlock(this.session);
    },
    togglePause() {
      if (this.paused) {
        this.recorder.resume();
        this.paused = false;
        return;
      }

      this.recorder.pause();
      this.paused = true;
    },
    stop() {
      this.recording = false;
      this.paused = false;
      this.$emit('recording', false);
      clearInterval(this.timer);

      if (this.recorder && this.recorder.state !== 'inactive') {
        this.recorder.stop();
      }

      this.stopMeter();
      this.releaseStream();
    },
    // nível do microfone ao vivo: o profissional vê que a captação está entrando
    startMeter() {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;

      if (!AudioContextClass) {
        return;
      }

      this.audioContext = new AudioContextClass();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;
      this.sourceNode = this.audioContext.createMediaStreamSource(this.stream);
      this.sourceNode.connect(this.analyser);
      this.meterData = new Uint8Array(this.analyser.frequencyBinCount);

      this.meterTimer = setInterval(this.readLevel, 80);
    },
    readLevel() {
      if (!this.analyser || this.paused) {
        this.level = 0;
        return;
      }

      this.analyser.getByteTimeDomainData(this.meterData);

      let peak = 0;

      for (let i = 0; i < this.meterData.length; i += 1) {
        const value = Math.abs(this.meterData[i] - 128) / 128;

        if (value > peak) {
          peak = value;
        }
      }

      this.level = Math.min(Math.round(peak * 160), 100);
    },
    stopMeter() {
      clearInterval(this.meterTimer);
      this.level = 0;
      this.analyser = null;
      this.meterData = null;

      if (this.sourceNode) {
        this.sourceNode.disconnect();
        this.sourceNode = null;
      }

      if (this.audioContext) {
        this.audioContext.close().catch(() => {});
        this.audioContext = null;
      }
    },
    // o indicador de microfone do navegador só apaga quando toda track termina
    releaseStream() {
      if (this.recorder && this.recorder.state !== 'inactive') {
        this.recorder.stop();
      }

      this.recorder = null;

      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
      }
    },
    async uploadFile(e) {
      const [file] = e.target.files || [];
      e.target.value = '';

      if (!file) {
        return;
      }

      const seconds = await this.readDuration(file);

      this.upload(file, file.name, seconds);
    },
    // a duração do arquivo é medida aqui porque nem todo modelo de transcrição
    // devolve o tamanho do áudio, e é por ela que o consumo é contado
    readDuration(file) {
      return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const audio = new Audio();

        const finish = (value) => {
          URL.revokeObjectURL(url);
          resolve(value);
        };

        audio.addEventListener('loadedmetadata', () => {
          finish(Number.isFinite(audio.duration) ? Math.round(audio.duration) : 0);
        });
        audio.addEventListener('error', () => finish(0));

        audio.src = url;
      });
    },
    upload(blob, fileName, seconds = 0) {
      const formData = new FormData();
      formData.append('file', blob, fileName);

      if (seconds > 0) {
        formData.append('seconds', seconds);
      }

      this.uploading = true;

      return this.$http
        .post(`/medical-records/${this.medicalRecordId}/transcriptions`, formData)
        .then(({ data }) => {
          this.context.items = [...this.items, data];
          this.schedulePoll();
          // cada trecho enviado consome saldo: atualiza a franquia sem esperar o polling
          this.load(true);
        })
        .catch(this.showError)
        .finally(() => {
          this.uploading = false;
        });
    },
    flush() {
      if (this.sending || this.queue.length === 0) {
        return;
      }

      const [item] = this.queue;

      this.sending = true;
      clearTimeout(this.retryTimer);

      this.send(item)
        .then(({ data }) => {
          this.queue.shift();
          this.sending = false;

          if (item.medicalRecordId === this.medicalRecordId) {
            this.replaceItem(data);
          }

          this.flush();
        })
        .catch((e) => {
          this.sending = false;
          this.sendFailed(item, e);
        });
    },
    send(item) {
      const url = `/medical-records/${item.medicalRecordId}/transcriptions/sessions/${item.sessionId}`;

      if (item.finish) {
        return this.$http.post(`${url}/finish`);
      }

      const formData = new FormData();
      formData.append('file', item.blob, item.fileName);
      formData.append('seconds', item.seconds);

      return this.$http.post(`${url}/parts/${item.part}`, formData);
    },
    sendFailed(item, e) {
      const status = e && e.response ? e.response.status : 0;

      if (!status || status >= 500 || RETRY_STATUSES.includes(status)) {
        this.retryTimer = setTimeout(this.flush, RETRY_INTERVAL);
        return;
      }

      this.droppedSession = item.sessionId;
      this.queue = this.queue.filter(current => current.sessionId !== item.sessionId);

      if (this.recording && this.session && this.session.id === item.sessionId) {
        this.stop();
      }

      this.showError(e);
      this.flush();
    },
    replaceItem(data) {
      const exists = this.items.some(item => item.id === data.id);

      this.context.items = exists
        ? this.items.map(item => (item.id === data.id ? data : item))
        : [...this.items, data];

      this.schedulePoll();
    },
    reprocess(item) {
      this.$http
        .post(`/medical-record-transcriptions/${item.id}/reprocess`)
        .then(() => this.load(true))
        .catch(this.showError);
    },
    canReprocess(item) {
      return item.hasAudio && !['processing', 'recording'].includes(item.status) && !this.readonly;
    },
    removeTooltip(item) {
      if (this.recording) {
        return 'Pare a gravação para excluir';
      }

      return item.status === 'processing'
        ? 'Aguarde a transcrição terminar'
        : 'Excluir gravação';
    },
    remove(item) {
      this.$dialog.show('', {
        html:
          '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + '<div>Excluir a gravação, o áudio e a transcrição?</div>'
          + '<div class="rt-dialog-warning">Não é possível desfazer</div>'
          + '</div>',
        buttons: [
          {
            label: 'Não',
            classes: '',
          }, {
            label: 'Sim, excluir',
            classes: 'btn-error ml-2',
            // devolver a promise faz o diálogo mostrar o loading e travar os botões
            click: close => this.$http
              .delete(`/medical-record-transcriptions/${item.id}`)
              .then(() => {
                this.context.items = this.items.filter(current => current.id !== item.id);
                this.$toast.success('Gravação excluída');

                // sem gravações, a autorização é apagada no servidor: recarrega para refletir
                if (this.items.length === 0) {
                  this.load(true);
                }
              })
              .catch(this.showError)
              .finally(close),
          },
        ],
      });
    },
    // o link do áudio é temporário: cada clique pede um novo antes de tocar na própria tela
    play(item) {
      this.expanded = item.id;

      return this.$httpX
        .get(`/medical-record-transcriptions/${item.id}/audio`)
        .then(({ data }) => {
          this.$set(this.audioUrls, item.id, data.url);
        })
        .catch(this.showError);
    },
    toggle(item) {
      this.expanded = this.expanded === item.id ? null : item.id;
      this.showText = null;
    },
    review(item) {
      this.reviewSummary = { content: this.parseNote(item.note) };
    },
    apply(sections) {
      this.reviewSummary = null;
      this.$emit('apply', sections);
    },
    // a nota vem em seções com título em caixa alta; o revisor casa cada seção
    // com o campo correspondente da anamnese
    parseNote(note) {
      const content = {};
      let current = null;

      String(note || '').split('\n').forEach((line) => {
        const value = line.trim();

        if (!value) {
          return;
        }

        if (/^[A-ZÁÀÂÃÉÊÍÓÔÕÚÇ\s/]+$/.test(value) && value.length < 60) {
          current = value.charAt(0) + value.slice(1).toLowerCase();
          content[current] = '';
          return;
        }

        if (current) {
          content[current] = `${content[current]}${content[current] ? '\n' : ''}${value}`;
        }
      });

      // seção que a IA marcou como não mencionada só ocuparia espaço na revisão
      const filled = Object.keys(content).reduce((acc, key) => {
        const value = content[key].trim();

        if (value && !/^-?\s*não mencionado/i.test(value)) {
          acc[key] = value;
        }

        return acc;
      }, {});

      return Object.keys(filled).length > 0 ? filled : note;
    },
    // nova aba para não interromper o atendimento (a gravação vive nesta página)
    openMinutes() {
      const route = this.$router.resolve('/customer/ai-usage');

      window.open(route.href, '_blank');
    },
    refreshQuota() {
      this.refreshing = true;

      return this.load(true).finally(() => {
        this.refreshing = false;
      });
    },
    durationLabel(seconds) {
      const minutes = Math.floor(seconds / 60);
      const rest = Math.round(seconds % 60);

      return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
    },
    extensionFor(mimeType) {
      if (/mp4/.test(mimeType)) {
        return 'mp4';
      }

      return /ogg/.test(mimeType) ? 'ogg' : 'webm';
    },
    statusLabel(item) {
      return STATUS_LABELS[item.status] || item.status;
    },
    statusIcon(item) {
      return STATUS_ICONS[item.status] || 'file-audio';
    },
    // gravação em andamento só existe na memória do navegador: sair da página perde
    // o bloco atual, e o aviso do navegador dá a chance de voltar atrás
    warnOnLeave(e) {
      if (!this.recording && this.queue.length === 0) {
        return undefined;
      }

      e.preventDefault();
      e.returnValue = '';

      return '';
    },
    clearTimers() {
      clearInterval(this.timer);
      clearTimeout(this.poll);
    },
    showError(e) {
      const message = e && e.response && e.response.data && e.response.data.message;
      this.$toast.show(message || 'Não foi possível concluir a operação', { type: 'error' });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables.scss";

$rt-amber-bg: #FFF8E6;
$rt-amber-border: #F2C94C;
$rt-amber-text: #8A6B10;
$rt-green-bg: #F2FAF5;
$rt-green-border: #BFE3CD;
$rt-green-text: #1F7A4E;

.record-transcription {
  .rt-head {
    margin-bottom: $layout-spacing-lg;

    .rt-eyebrow {
      color: $primary-color;
      font-size: $font-size-xs;
      font-weight: 700;
      letter-spacing: .05rem;
      text-transform: uppercase;
    }

    h6 {
      color: $gray-color-900;
      font-weight: 700;
      margin: $unit-1 0 $unit-1;
    }

    p {
      color: $gray-color-dark;
      font-size: $font-size-sm;
      margin: 0;
      max-width: 34rem;
    }
  }

  .rt-plan {
    align-items: center;
    background: lighten($primary-color, 36%);
    border-radius: $border-radius;
    color: $primary-color;

    &.warning {
      background: #FFF8E6;
      border: $border-width solid #F2C94C;
      color: #8A6B10;
    }

    &.exceeded {
      background: #FFF5F5;
      border: $border-width solid lighten($error-color, 15%);
      color: $red-color-700;
    }
    display: flex;
    gap: $layout-spacing;
    margin-bottom: $layout-spacing-lg;
    padding: $unit-2 $layout-spacing-lg;

    .rt-plan-text {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: $unit-2;

      span {
        color: $gray-color-dark;
      }

      .btn-action {
        background: transparent;
        border-color: transparent;
        box-shadow: none;
        color: inherit;
        height: .9rem;
        min-width: .9rem;

        &:hover {
          background: rgba(0, 0, 0, .05);
        }
      }
    }

    .rt-plan-actions {
      align-items: center;
      display: flex;
      gap: $unit-1;
      margin-left: auto;
      white-space: nowrap;

      .rt-plan-buy {
        color: $secondary-color;
        font-weight: 700;

        &:focus {
          box-shadow: none;
        }
      }

      .btn-action {
        background: transparent;
        border-color: transparent;
        box-shadow: none;
        color: inherit;

        &:hover {
          background: rgba(0, 0, 0, .05);
        }
      }
    }
  }

  .rt-consent {
    background: $rt-amber-bg;
    border: $border-width solid $rt-amber-border;
    border-radius: $border-radius;
    display: flex;
    gap: $layout-spacing-lg;
    margin-bottom: $layout-spacing-lg;
    padding: $layout-spacing-lg;

    .rt-consent-icon {
      align-items: center;
      background: $light-color;
      border-radius: 50%;
      box-shadow: 0 1px 3px rgba(0, 0, 0, .08);
      color: $rt-amber-text;
      display: flex;
      flex: 0 0 auto;
      height: 1.8rem;
      justify-content: center;
      width: 1.8rem;
    }

    .rt-consent-body {
      flex: 1;

      h6 {
        color: $gray-color-900;
        font-weight: 700;
        margin: 0;
      }

      .rt-consent-help {
        color: $gray-color-dark;
        font-size: $font-size-sm;
        margin: $unit-1 0 $unit-2;
      }
    }

    .rt-term {
      background: $light-color;
      border: $border-width solid rgba(242, 201, 76, .5);
      border-radius: $border-radius;
      color: $gray-color-900;
      font-style: italic;
      margin: 0 0 $layout-spacing;
      padding: $layout-spacing;
    }

    .rt-check {
      font-size: $font-size-sm;
      margin: 0;
    }

    .form-input-hint {
      margin-bottom: 0;
    }
  }

  .rt-recorder {
    background: $gray-color-ultra-light;
    border: $border-width solid $border-color;
    border-radius: $border-radius;
    margin-bottom: $layout-spacing-lg;

    .rt-recorder-head {
      align-items: center;
      border-bottom: $border-width solid $border-color;
      display: flex;
      gap: $layout-spacing;
      justify-content: space-between;
      padding: $unit-2 $layout-spacing-lg;
    }

    .rt-recorder-body {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: $layout-spacing-lg;
      padding: $layout-spacing-lg;
    }

    .rt-recorder-foot {
      border-top: $border-width dashed $border-color;
      color: $gray-color-dark;
      font-size: $font-size-xs;
      padding: $unit-2 $layout-spacing-lg;
    }
  }

  .rt-timer {
    align-items: center;
    display: flex;
    gap: $unit-2;

    strong {
      color: $gray-color-900;
      font-size: 1rem;
      font-variant-numeric: tabular-nums;
      font-weight: 700;
    }

    .rt-timer-state {
      color: $gray-color-dark;
      font-size: $font-size-sm;
    }
  }

  .rt-dot {
    background: $gray-color;
    border-radius: 50%;
    flex: 0 0 auto;
    height: .5rem;
    width: .5rem;

    &.live {
      animation: rt-pulse 1.4s infinite;
      background: $error-color;
    }
  }

  .rt-meter {
    display: flex;
    gap: 2px;
    margin-right: auto;

    span {
      background: darken($gray-color-ultra-light, 6%);
      border-radius: 1px;
      height: .5rem;
      width: 3px;
    }

    &.live span.on {
      background: $success-color;
    }
  }

  .rt-actions {
    display: flex;
    gap: $layout-spacing;
  }

  .rt-file {
    cursor: pointer;
    overflow: hidden;
    position: relative;

    input {
      cursor: pointer;
      font-size: 0;
      height: 100%;
      left: 0;
      opacity: 0;
      position: absolute;
      top: 0;
      width: 100%;
    }
  }

  .rt-uploading {
    align-items: center;
    background: $light-color;
    border: $border-width solid $border-color;
    border-radius: $border-radius;
    color: $gray-color-dark;
    display: flex;
    font-size: $font-size-sm;
    gap: $layout-spacing-lg;
    margin-bottom: $layout-spacing-lg;
    padding: $layout-spacing $layout-spacing-lg;

    .loading {
      flex: 0 0 auto;
      margin: 0;
      min-width: .8rem;
    }
  }

  .rt-live {
    align-items: center;
    color: $error-color;
    display: flex;
    font-weight: 600;
    gap: $unit-2;
    margin-left: auto;
    text-transform: none;
  }

  .rt-list {
    border: $border-width solid $border-color;
    border-radius: $border-radius;

    .rt-list-head {
      align-items: center;
      background: $gray-color-ultra-light;
      border-bottom: $border-width solid $border-color;
      color: $gray-color-dark;
      display: flex;
      font-size: $font-size-sm;
      font-weight: 600;
      gap: $unit-2;
      padding: $unit-2 $layout-spacing-lg;
    }

    .rt-count {
      background: $light-color;
      border: $border-width solid $border-color;
      border-radius: 1rem;
      font-variant-numeric: tabular-nums;
      padding: 0 $unit-2;
    }
  }

  .rt-item + .rt-item {
    border-top: $border-width solid $border-color;
  }

  .rt-row {
    align-items: center;
    cursor: pointer;
    display: flex;
    gap: $layout-spacing;
    padding: $unit-2 $layout-spacing-lg;

    &:hover {
      background: $gray-color-ultra-light;
    }
  }

  .rt-row-icon {
    color: $gray-color-dark;

    &.done {
      color: $rt-green-text;
    }

    &.failed {
      color: $error-color;
    }

    &.processing {
      color: $primary-color;
    }
  }

  .rt-row-info {
    display: flex;
    flex-direction: column;
    margin-right: auto;

    strong {
      color: $gray-color-900;
    }

    small {
      color: $gray-color-dark;
      font-variant-numeric: tabular-nums;
    }
  }

  .rt-row-actions {
    display: flex;
    gap: $unit-1;

    .btn {
      background: transparent;
      border-color: transparent;
      box-shadow: none;
      color: $gray-color-dark;

      &:hover,
      &:focus {
        background: darken($gray-color-ultra-light, 4%);
        border-color: transparent;
        box-shadow: none;
        color: $gray-color-900;
      }
    }

    .rt-danger:hover {
      background: lighten($error-color, 42%);
      color: $error-color;
    }
  }

  .rt-row-chevron {
    color: $gray-color;
    transition: transform .2s;
  }

  .rt-item.expanded .rt-row-chevron {
    transform: rotate(90deg);
  }

  .rt-pill {
    align-items: center;
    background: $light-color;
    border: $border-width solid $border-color;
    border-radius: 1rem;
    color: $gray-color-dark;
    display: inline-flex;
    font-size: $font-size-sm;
    gap: $unit-1;
    padding: 0 $unit-2;
    white-space: nowrap;

    &.done {
      background: $rt-green-bg;
      border-color: $rt-green-border;
      color: $rt-green-text;
    }

    &.failed {
      border-color: lighten($error-color, 25%);
      color: $error-color;
    }

    &.processing {
      border-color: lighten($primary-color, 30%);
      color: $primary-color;
    }

    &.pending {
      background: $rt-amber-bg;
      border-color: $rt-amber-border;
      color: $rt-amber-text;
    }
  }

  .rt-detail {
    background: $gray-color-ultra-light;
    border-top: $border-width dashed $border-color;
    padding: $layout-spacing-lg;
  }

  .rt-audio {
    display: block;
    height: 1.6rem;
    margin-bottom: $layout-spacing;
    width: 100%;
  }

  .rt-detail-title {
    align-items: center;
    color: $gray-color-900;
    display: flex;
    font-weight: 700;
    gap: $unit-2;
    margin-bottom: $unit-2;
  }

  .rt-tag {
    background: $rt-amber-bg;
    border: $border-width solid $rt-amber-border;
    border-radius: $border-radius;
    color: $rt-amber-text;
    font-size: $font-size-xs;
    font-weight: 700;
    padding: 0 $unit-1;
    text-transform: uppercase;
  }

  .rt-text {
    background: $light-color;
    border: $border-width solid $border-color;
    border-radius: $border-radius;
    line-height: 1.5;
    margin-bottom: $layout-spacing;
    padding: $layout-spacing;
    white-space: pre-line;

    &.raw {
      color: $gray-color-900;
      font-size: $font-size;
      margin-bottom: 0;
      max-height: 22rem;
      overflow-y: auto;
      padding: $layout-spacing-lg;
    }
  }

  .rt-raw {
    margin-top: $layout-spacing-lg;
  }

  .rt-raw-head {
    align-items: baseline;
    color: $gray-color-900;
    display: flex;
    font-weight: 700;
    gap: $unit-2;
    margin-bottom: $unit-2;

    small {
      color: $gray-color-dark;
      font-size: $font-size-xs;
      font-weight: 400;
    }
  }

  .rt-detail-actions {
    align-items: center;
    display: flex;
    gap: $layout-spacing;

    .btn-link:focus {
      box-shadow: none;
    }
  }

  .rt-detail-error,
  .rt-detail-waiting {
    align-items: center;
    background: $light-color;
    border: $border-width solid $border-color;
    border-radius: $border-radius;
    display: flex;
    font-size: $font-size-sm;
    gap: $layout-spacing-lg;
    padding: $layout-spacing $layout-spacing-lg;
  }

  .rt-detail-error {
    border-color: lighten($error-color, 30%);
    color: $error-color;
  }

  .rt-detail-waiting {
    color: $gray-color-dark;

    .loading {
      flex: 0 0 auto;
      margin: 0;
      min-width: .8rem;
    }
  }
}

.rt-dialog-warning {
  background: #FFF5F5;
  border: $border-width solid lighten($error-color, 15%);
  border-radius: $border-radius;
  color: $gray-color-900;
  font-weight: 600;
  margin-top: $layout-spacing;
  padding: $unit-2 $layout-spacing;
}

@keyframes rt-pulse {
  0% { opacity: 1; }
  50% { opacity: .3; }
  100% { opacity: 1; }
}
</style>
